import React, { useEffect, useState } from 'react';
import {
  ACCENT,
  ACCENT_LIGHT,
  ACCENT_BORDER,
  TEXT_DARK,
  TEXT_MID,
  FONT,
} from '../lib/tokens';
import { Ic } from '../components/Icons';
import { Card, ScreenWrap, Tag } from '../components/ui/Primitives';
import { supabase, hasSupabase } from '../lib/supabase';
import { RESOURCES as FALLBACK } from '../data/resources';
import { useBreakpoint } from '../lib/useBreakpoint';

export default function ResourcesScreen() {
  // Static fallback is the canonical source for the per-session prompt cards
  // (they reference inline text from /src/data/prompts.js). DB rows overlay
  // by id so an admin can edit copy or swap a download URL.
  const [items, setItems] = useState(FALLBACK);
  const [modalItem, setModalItem] = useState(null);
  const bp = useBreakpoint();
  const isDesktop = bp === 'desktop';

  useEffect(() => {
    if (!hasSupabase) return;
    supabase
      .from('resources')
      .select('*')
      .eq('active', true)
      .order('sort_order', { ascending: true })
      .then(({ data }) => {
        if (!data || data.length === 0) return;
        const byId = new Map(data.map((r) => [r.id, r]));
        setItems(FALLBACK.map((f) => (byId.has(f.id) ? { ...f, ...byId.get(f.id) } : f)));
      });
  }, []);

  const cardForItem = (r) =>
    r.kind === 'copy' ? (
      <CopyCard
        key={r.id}
        item={r}
        isDesktop={isDesktop}
        onOpenModal={() => setModalItem(r)}
      />
    ) : (
      <DownloadCard key={r.id} item={r} />
    );

  if (isDesktop) {
    return (
      <>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 16,
            alignItems: 'start',
          }}
        >
          {items.map(cardForItem)}
        </div>
        {modalItem && <PromptModal item={modalItem} onClose={() => setModalItem(null)} />}
      </>
    );
  }

  return <ScreenWrap>{items.map(cardForItem)}</ScreenWrap>;
}

function DownloadCard({ item }) {
  // Filename for the download attribute: derive from URL.
  const filename = (() => {
    if (!item.url) return undefined;
    const m = item.url.match(/\/([^/?#]+)$/);
    return m ? m[1] : undefined;
  })();
  const disabled = !item.url || item.url === '#';

  return (
    <Card style={{ padding: '13px 14px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 7,
        }}
      >
        <Tag>{item.tag || item.session_tag}</Tag>
        {!disabled ? (
          <a
            href={item.url}
            download={filename}
            rel="noopener noreferrer"
            aria-label={`Download ${item.title}`}
            style={{
              width: 28,
              height: 28,
              borderRadius: 7,
              background: ACCENT_LIGHT,
              border: `1px solid ${ACCENT_BORDER}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              cursor: 'pointer',
              textDecoration: 'none',
            }}
          >
            <Ic.Link size={13} color={ACCENT} />
          </a>
        ) : (
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 7,
              background: '#f1f5f9',
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Ic.Link size={13} color="#cbd5e1" />
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: TEXT_DARK,
          lineHeight: 1.3,
          marginBottom: 5,
        }}
      >
        {item.title}
      </div>
      <div style={{ fontSize: 12.5, color: TEXT_MID, lineHeight: 1.5 }}>{item.description}</div>
    </Card>
  );
}

function CopyCard({ item, isDesktop, onOpenModal }) {
  // On mobile we keep the inline expander (touch-friendly, no overlay).
  // On desktop we use a uniform-height card + a "View full prompt" modal so
  // the responsive grid doesn't get jagged rows.
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = (e) => {
    e.stopPropagation();
    if (!item.text) return;
    if (navigator.clipboard) navigator.clipboard.writeText(item.text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card style={{ padding: '13px 14px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 7,
        }}
      >
        <Tag>{item.tag}</Tag>
        <button
          onClick={copy}
          aria-label={`Copy ${item.title}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            background: copied ? '#dcfce7' : ACCENT_LIGHT,
            border: `1px solid ${copied ? '#86efac' : ACCENT_BORDER}`,
            borderRadius: 7,
            padding: '4px 9px',
            cursor: 'pointer',
            fontFamily: FONT,
            fontSize: 11,
            fontWeight: 600,
            color: copied ? '#16a34a' : ACCENT,
            transition: 'all 0.15s',
            flexShrink: 0,
          }}
        >
          {copied ? <Ic.Check size={11} color="#16a34a" /> : <Ic.Copy size={11} color={ACCENT} />}
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: TEXT_DARK,
          lineHeight: 1.3,
          marginBottom: 5,
        }}
      >
        {item.title}
      </div>
      <div style={{ fontSize: 12.5, color: TEXT_MID, lineHeight: 1.5 }}>{item.description}</div>

      <button
        onClick={() => (isDesktop ? onOpenModal() : setExpanded((v) => !v))}
        style={{
          marginTop: 9,
          background: 'none',
          border: 'none',
          padding: 0,
          color: ACCENT,
          fontSize: 11.5,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: FONT,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        {isDesktop ? 'View full prompt' : expanded ? 'Hide full prompt' : 'View full prompt'}
        {isDesktop ? (
          <Ic.ChevronRight size={12} color={ACCENT} />
        ) : expanded ? (
          <Ic.ChevronUp size={12} color={ACCENT} />
        ) : (
          <Ic.ChevronDown size={12} color={ACCENT} />
        )}
      </button>

      {!isDesktop && expanded && (
        <div
          style={{
            marginTop: 10,
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 9,
            padding: '11px 13px',
            fontSize: 12.5,
            color: '#475569',
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
          }}
        >
          {item.text}
        </div>
      )}
    </Card>
  );
}

function PromptModal({ item, onClose }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    if (!item.text) return;
    if (navigator.clipboard) navigator.clipboard.writeText(item.text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Esc to close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(10,16,32,0.55)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 14,
          width: '100%',
          maxWidth: 720,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
        }}
      >
        <div
          style={{
            padding: '16px 22px',
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <Tag>{item.tag}</Tag>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: TEXT_DARK,
                marginTop: 6,
                lineHeight: 1.3,
              }}
            >
              {item.title}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <Ic.Close size={16} color="#64748b" />
          </button>
        </div>
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '18px 22px',
            fontSize: 13.5,
            color: '#475569',
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
          }}
        >
          {item.text}
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid #f1f5f9',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={copy}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: copied ? '#dcfce7' : ACCENT,
              border: copied ? '1px solid #86efac' : 'none',
              borderRadius: 9,
              padding: '10px 16px',
              color: copied ? '#16a34a' : '#fff',
              fontFamily: FONT,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {copied ? <Ic.Check size={14} color="#16a34a" /> : <Ic.Copy size={14} color="#fff" />}
            {copied ? 'Copied to clipboard' : 'Copy prompt'}
          </button>
        </div>
      </div>
    </div>
  );
}
